/**
 * AppShell V3 — layout racine authentifié.
 *
 * Architecture V3.0 (PHASE 3.0 UX Revolution — DECISIONS #167) :
 *
 *   ┌──────────────┬──────────────────────────────────┐
 *   │ WidgetSidebar│  TopNav (fixed h-14, left=150px) │
 *   │  (150px fix) │──────────────────────────────────│
 *   │  W1-W9       │  <Outlet /> (scroll interne)     │
 *   └──────────────┴──────────────────────────────────┘
 *
 * - WidgetSidebar : position fixed left-0, width 150px (WIDGET_SIDEBAR_W)
 * - TopNav        : position fixed top-0, left = 150px
 * - Content       : margin-left = 150px + padding-top 56px (TopNav)
 *
 * V2 → V3 (PHASE 3.0 HOTFIX) :
 *   - Ancienne Sidebar accordéon (V2-DESIGN NAV.9) supprimée.
 *   - WidgetSidebar est désormais le seul élément sidebar (9 widgets Smart + Classique).
 *   - sidebarStore/useSidebarStore supprimés de AppShell (plus de collapse variable).
 *   - totalLeft = WIDGET_SIDEBAR_W (constante 150px).
 */

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { TopNav } from './TopNav';
import { WidgetSidebar, WIDGET_SIDEBAR_W } from './WidgetSidebar';
import { useScopeBarPrefetch } from '@/modules/marche/hooks/useScopeBarPrefetch';
import { useAuth } from '@/hooks/useAuth';

export function AppShell() {
  const totalLeft = WIDGET_SIDEBAR_W;
  const location  = useLocation();
  const { fetchMe } = useAuth();

  // D-SESSION-PERMISSIONS-REFRESH — rafraîchit user.permissions au montage.
  // Échec réseau = silencieux (session courante conservée).
  useEffect(() => { fetchMe().catch(() => {}); }, [fetchMe]);

  // Préchauffe les 4 référentiels de la ScopeBar MX RM Flow dès l'AppShell
  // (post-auth) pour éviter le pile-up HTTP/1.1 au mount de /marche.
  useScopeBarPrefetch();

  return (
    <div
      data-testid="app-shell"
      className="min-h-screen bg-[var(--surface-bg)] text-[var(--text-primary)]"
    >
      {/* TopNav fixe haut — left = WIDGET_SIDEBAR_W (150px) */}
      <TopNav />

      {/* WidgetSidebar fixe — 9 widgets, position left-0, top 56px */}
      <WidgetSidebar sidebarW={0} />

      {/* Zone contenu — flex-col, overflow-hidden → 0 scroll externe */}
      <motion.div
        data-testid="main-content"
        initial={false}
        animate={{ marginLeft: totalLeft }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="flex h-screen flex-col overflow-x-hidden pt-14"
      >
        {/* Transition entre routes — balayage depuis la sidebar widget */}
        <AnimatePresence mode="wait">
          <motion.main
            key={location.pathname}
            className="flex-1 min-h-0 overflow-y-auto p-4"
            initial={{ opacity: 0, x: -52, filter: 'blur(3px)' }}
            animate={{ opacity: 1, x: 0,   filter: 'blur(0px)' }}
            exit={{ opacity: 0, x: 18, transition: { duration: 0.14, ease: 'easeIn' as const } }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] as const }}
          >
            <Outlet />
          </motion.main>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
